<template>
  <div
    ref="convstooltip"
    class="con-vs-tooltip"
    @mouseleave="mouseleavex"
    @mouseenter="mouseenterx"
    @mouseup="destroy"
    @touchstart.stop="handleTouchStart"
    @touchend.stop="handleTouchEnd"
    @click="handleClick">
    <transition name="tooltip-fade">
      <div
        v-show="active && (!isTouchDevice || allowTouch)"
        ref="vstooltip"
        :class="[`vs-tooltip-${positionx || position}`,`vs-tooltip-${color}`, {'after-none': noneAfter, 'vs-tooltip-allow-touch': allowTouch}]"
        :style="style"
        class="vs-tooltip">
        <h4 v-if="title">{{ title }}</h4>
        <slot name="inner">{{ text }}</slot>
      </div>
    </transition>
    <slot></slot>
  </div>
</template>
<script>
import utils from '@/assets/utils';
import _color from '@/assets/utils/color.js';

export default {
  name: 'vx-tooltip',
  props: {
    title: {
      default: null,
      type: [String, Number],
    },
    text: {
      default: null,
      type: [String, Number],
    },
    color: {
      default: null,
      type: String,
    },
    position: {
      default: 'top',
      type: String,
    },
    delay: {
      default: '0s',
      type: [Number, String],
    },
    activeProp: {
      type: Boolean,
      required: false,
      default: false,
    },
    allowTouch: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      cords: {},
      active: this.activeProp,
      widthx: 'auto',
      positionx: null,
      noneAfter: false,
      isTouchDevice: false,
      outsideInteractionHandler: null,
    };
  },
  computed: {
    style() {
      return {
        left: this.cords.left,
        top: this.cords.top,
        transitionDelay: this.active ? this.delay : '0s',
        background: _color.getColor(this.color, 1),
        width: this.widthx,
      };
    },
  },
  watch: {
    activeProp(value) {
      this.active = value;
    },
  },
  mounted() {
    // Detect touch device on mount
    this.isTouchDevice = ('ontouchstart' in window) 
      || (navigator.maxTouchPoints > 0) 
      || (navigator.msMaxTouchPoints > 0);
  },
  beforeDestroy() {
    this.removeOutsideInteractionListeners();
    this.$nextTick(() => {
      if (this.$refs.vstooltip) {
        utils.removeBody(this.$refs.vstooltip);
      }
    });
  },
  methods: {
    handleTouchStart(event) {
      if (!this.allowTouch) return;
      this.isTouchDevice = true;
      event.preventDefault();
      event.stopPropagation();
      this.toggleTooltip();
    },
    handleTouchEnd(event) {
      if (!this.allowTouch) return;
      event.preventDefault();
      event.stopPropagation();
    },
    handleClick(event) {
      if (!this.allowTouch || !this.isTouchDevice) {
        if (this.active) {
          this.mouseleavex();
        } else {
          this.mouseenterx();
        }
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      this.toggleTooltip();
    },
    toggleTooltip() {
      if (this.active) {
        this.destroy();
      } else {
        this.showTooltip();
      }
    },
    showTooltip() {
      this.active = true;
      this.$nextTick(() => {
        utils.insertBody(this.$refs.vstooltip);
        this.changePosition(this.$refs.convstooltip, this.$refs.vstooltip);
        this.registerOutsideInteractionListeners();
      });
    },
    mouseenterx() {
      // Don't show tooltip on touch devices unless allowed
      if (this.isTouchDevice && !this.allowTouch) return;
      
      this.showTooltip();
    },
    mouseleavex() {
      this.active = this.activeProp;
    },
    changePosition(elxEvent, tooltip) {
      this.noneAfter = false;
      this.positionx = null;
      const elx = elxEvent.closest('.con-vs-tooltip');
      const scrollTopx = window.pageYOffset || document.documentElement.scrollTop;
      let topx = elx.getBoundingClientRect().top + scrollTopx - tooltip.clientHeight - 4;
      let leftx = elx.getBoundingClientRect().left - tooltip.clientWidth / 2 + elx.clientWidth / 2;
      const widthx = elx.clientWidth;

      if (this.position == 'bottom') {
        topx = elx.getBoundingClientRect().top + scrollTopx + elx.clientHeight + 4;
      } else if (this.position == 'left') {
        leftx = elx.getBoundingClientRect().left - tooltip.clientWidth - 4;
        topx = elx.getBoundingClientRect().top + scrollTopx + (elx.clientHeight / 2) - (tooltip.clientHeight / 2);
        if (Math.sign(leftx) == -1) {
          leftx = elx.getBoundingClientRect().left;
          topx = elx.getBoundingClientRect().top + scrollTopx + elx.clientHeight + 4;
          this.positionx = 'bottom';
          this.noneAfter = true;
        }
      } else if (this.position == 'right') {
        leftx = elx.getBoundingClientRect().left + elx.clientWidth + 4;
        topx = elx.getBoundingClientRect().top + scrollTopx + (elx.clientHeight / 2) - (tooltip.clientHeight / 2);
        if (window.innerWidth - (leftx + tooltip.clientWidth) <= 20) {
          leftx = elx.getBoundingClientRect().left - tooltip.clientWidth / 2 - 10;
          topx = elx.getBoundingClientRect().top + scrollTopx + elx.clientHeight + 4;
          this.positionx = 'bottom';
          this.noneAfter = true;
        }
      }
      this.cords = {
        left: `${leftx}px`,
        top: `${topx}px`,
        width: `${widthx}px`,
      };
    },
    destroy() {
      this.active = false;
      this.removeOutsideInteractionListeners();
      this.$nextTick(() => {
        if (this.$refs.vstooltip) {
          utils.removeBody(this.$refs.vstooltip);
        }
      });
    },
    registerOutsideInteractionListeners() {
      if (!this.allowTouch || this.outsideInteractionHandler || !this.isTouchDevice) return;

      this.outsideInteractionHandler = (event) => {
        const tooltipEl = this.$refs.vstooltip;
        const triggerEl = this.$refs.convstooltip;

        if (!tooltipEl || !triggerEl) return;
        if (tooltipEl.contains(event.target) || triggerEl.contains(event.target)) {
          return;
        }

        this.destroy();
      };

      document.addEventListener('touchstart', this.outsideInteractionHandler, true);
      document.addEventListener('click', this.outsideInteractionHandler, true);
    },
    removeOutsideInteractionListeners() {
      if (!this.outsideInteractionHandler) return;

      document.removeEventListener('touchstart', this.outsideInteractionHandler, true);
      document.removeEventListener('click', this.outsideInteractionHandler, true);
      this.outsideInteractionHandler = null;
    },
  },
};
</script>
